import { supabase } from '../lib/supabase';

export const complianceReportsCustomizationService = {
  // ============================================================================
  // REPORT TEMPLATES
  // ============================================================================

  async getAllTemplates() {
    const { data, error } = await supabase?.from('report_templates')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      templateName: row?.template_name,
      reportType: row?.report_type,
      description: row?.description,
      includeViolationTrends: row?.include_violation_trends,
      includeDepartmentMetrics: row?.include_department_metrics,
      includeCostAnalysis: row?.include_cost_analysis,
      includeDlirReadiness: row?.include_dlir_readiness,
      customSections: row?.custom_sections,
      filterCriteria: row?.filter_criteria,
      createdBy: row?.created_by,
      isDefault: row?.is_default,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async createTemplate(template) {
    const { data, error } = await supabase?.from('report_templates')?.insert({
        template_name: template?.templateName,
        report_type: template?.reportType,
        description: template?.description,
        include_violation_trends: template?.includeViolationTrends ?? true,
        include_department_metrics: template?.includeDepartmentMetrics ?? true,
        include_cost_analysis: template?.includeCostAnalysis ?? true,
        include_dlir_readiness: template?.includeDlirReadiness ?? true,
        custom_sections: template?.customSections,
        filter_criteria: template?.filterCriteria,
        created_by: template?.createdBy,
        is_default: template?.isDefault ?? false
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      templateName: data?.template_name,
      reportType: data?.report_type
    };
  },

  // ============================================================================
  // SCHEDULED DELIVERIES
  // ============================================================================

  async getAllScheduledDeliveries() {
    const { data, error } = await supabase?.from('scheduled_report_deliveries')?.select(`
        *,
        report_templates (
          template_name,
          report_type
        )
      `)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      scheduleName: row?.schedule_name,
      reportTemplateId: row?.report_template_id,
      deliveryFrequency: row?.delivery_frequency,
      deliveryDayOfWeek: row?.delivery_day_of_week,
      deliveryDayOfMonth: row?.delivery_day_of_month,
      deliveryTime: row?.delivery_time,
      reportFormat: row?.report_format,
      recipientEmails: row?.recipient_emails || [],
      recipientDepartments: row?.recipient_departments || [],
      nextDeliveryDate: row?.next_delivery_date,
      lastDeliveryDate: row?.last_delivery_date,
      isActive: row?.is_active,
      createdBy: row?.created_by,
      template: row?.report_templates ? {
        templateName: row?.report_templates?.template_name,
        reportType: row?.report_templates?.report_type
      } : null,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async createScheduledDelivery(delivery) {
    const { data, error } = await supabase?.from('scheduled_report_deliveries')?.insert({
        schedule_name: delivery?.scheduleName,
        report_template_id: delivery?.reportTemplateId,
        delivery_frequency: delivery?.deliveryFrequency,
        delivery_day_of_week: delivery?.deliveryDayOfWeek,
        delivery_day_of_month: delivery?.deliveryDayOfMonth,
        delivery_time: delivery?.deliveryTime || '09:00:00',
        report_format: delivery?.reportFormat || 'pdf',
        recipient_emails: delivery?.recipientEmails,
        recipient_departments: delivery?.recipientDepartments,
        next_delivery_date: delivery?.nextDeliveryDate,
        is_active: delivery?.isActive ?? true,
        created_by: delivery?.createdBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      scheduleName: data?.schedule_name,
      deliveryFrequency: data?.delivery_frequency
    };
  },

  async updateScheduledDelivery(id, updates) {
    const { data, error } = await supabase?.from('scheduled_report_deliveries')?.update({
        schedule_name: updates?.scheduleName,
        delivery_frequency: updates?.deliveryFrequency,
        delivery_day_of_week: updates?.deliveryDayOfWeek,
        delivery_day_of_month: updates?.deliveryDayOfMonth,
        delivery_time: updates?.deliveryTime,
        report_format: updates?.reportFormat,
        recipient_emails: updates?.recipientEmails,
        recipient_departments: updates?.recipientDepartments,
        next_delivery_date: updates?.nextDeliveryDate,
        is_active: updates?.isActive
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      isActive: data?.is_active
    };
  },

  // ============================================================================
  // ABLE PAYROLL IMPORTS
  // ============================================================================

  async getAllAbleImports() {
    const { data, error } = await supabase?.from('able_payroll_imports')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      importSessionName: row?.import_session_name,
      payPeriodStart: row?.pay_period_start,
      payPeriodEnd: row?.pay_period_end,
      importStatus: row?.import_status,
      fileName: row?.file_name,
      fileUrl: row?.file_url,
      fileSizeBytes: row?.file_size_bytes,
      totalRecords: row?.total_records,
      processedRecords: row?.processed_records,
      failedRecords: row?.failed_records,
      validationErrors: row?.validation_errors,
      importedBy: row?.imported_by,
      importedAt: row?.imported_at,
      validatedAt: row?.validated_at,
      completedAt: row?.completed_at,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async createAbleImport(importData) {
    const { data, error } = await supabase?.from('able_payroll_imports')?.insert({
        import_session_name: importData?.importSessionName,
        pay_period_start: importData?.payPeriodStart,
        pay_period_end: importData?.payPeriodEnd,
        import_status: importData?.importStatus || 'pending',
        file_name: importData?.fileName,
        file_url: importData?.fileUrl,
        file_size_bytes: importData?.fileSizeBytes,
        imported_by: importData?.importedBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      importSessionName: data?.import_session_name,
      importStatus: data?.import_status
    };
  },

  async updateAbleImportStatus(id, status, updates = {}) {
    const updateData = {
      import_status: status
    };

    if (status === 'processing') {
      updateData.imported_at = new Date()?.toISOString();
    } else if (status === 'validated') {
      updateData.validated_at = new Date()?.toISOString();
    } else if (status === 'completed') {
      updateData.completed_at = new Date()?.toISOString();
    }

    if (updates?.totalRecords) updateData.total_records = updates?.totalRecords;
    if (updates?.processedRecords) updateData.processed_records = updates?.processedRecords;
    if (updates?.failedRecords) updateData.failed_records = updates?.failedRecords;
    if (updates?.validationErrors) updateData.validation_errors = updates?.validationErrors;

    const { data, error } = await supabase?.from('able_payroll_imports')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      importStatus: data?.import_status
    };
  },

  async getAbleImportRecords(importSessionId) {
    const { data, error } = await supabase?.from('able_payroll_import_records')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.eq('import_session_id', importSessionId);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      importSessionId: row?.import_session_id,
      employeeId: row?.employee_id,
      employeeNumber: row?.employee_number,
      employeeName: row?.employee_name,
      department: row?.department,
      grossPay: row?.gross_pay,
      basePay: row?.base_pay,
      overtimePay: row?.overtime_pay,
      allowances: row?.allowances,
      deductions: row?.deductions,
      swtAmount: row?.swt_amount,
      nasfundEmployee: row?.nasfund_employee,
      nasfundEmployer: row?.nasfund_employer,
      netPay: row?.net_pay,
      hoursWorked: row?.hours_worked,
      overtimeHours: row?.overtime_hours,
      validationStatus: row?.validation_status,
      validationMessages: row?.validation_messages || [],
      rawData: row?.raw_data,
      employee: row?.employees,
      createdAt: row?.created_at
    })) || [];
  },

  async createAbleImportRecord(record) {
    const { data, error } = await supabase?.from('able_payroll_import_records')?.insert({
        import_session_id: record?.importSessionId,
        employee_id: record?.employeeId,
        employee_number: record?.employeeNumber,
        employee_name: record?.employeeName,
        department: record?.department,
        gross_pay: record?.grossPay,
        base_pay: record?.basePay,
        overtime_pay: record?.overtimePay,
        allowances: record?.allowances,
        deductions: record?.deductions,
        swt_amount: record?.swtAmount,
        nasfund_employee: record?.nasfundEmployee,
        nasfund_employer: record?.nasfundEmployer,
        net_pay: record?.netPay,
        hours_worked: record?.hoursWorked,
        overtime_hours: record?.overtimeHours,
        validation_status: record?.validationStatus || 'passed',
        validation_messages: record?.validationMessages,
        raw_data: record?.rawData
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      validationStatus: data?.validation_status
    };
  },

  async validateAblePayrollRecord(record) {
    const { data, error } = await supabase?.rpc('validate_able_payroll_record', {
      p_gross_pay: record?.grossPay,
      p_base_pay: record?.basePay,
      p_swt_amount: record?.swtAmount,
      p_nasfund_employee: record?.nasfundEmployee,
      p_nasfund_employer: record?.nasfundEmployer,
      p_hours_worked: record?.hoursWorked
    });
    
    if (error) throw error;
    
    return data?.[0] || { validation_status: 'passed', validation_messages: [] };
  },

  // ============================================================================
  // IRC SWT FORM SESSIONS
  // ============================================================================

  async getAllIrcSwtSessions() {
    const { data, error } = await supabase?.from('irc_swt_form_sessions')?.select(`
        *,
        able_payroll_imports (
          import_session_name,
          pay_period_start,
          pay_period_end
        )
      `)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      sessionName: row?.session_name,
      payPeriodStart: row?.pay_period_start,
      payPeriodEnd: row?.pay_period_end,
      formStatus: row?.form_status,
      ableImportId: row?.able_import_id,
      payrollRunId: row?.payroll_run_id,
      totalEmployees: row?.total_employees,
      totalTaxableIncome: row?.total_taxable_income,
      totalSwtWithheld: row?.total_swt_withheld,
      formUrl: row?.form_url,
      generatedBy: row?.generated_by,
      generatedAt: row?.generated_at,
      validatedAt: row?.validated_at,
      submittedAt: row?.submitted_at,
      ircReferenceNumber: row?.irc_reference_number,
      validationErrors: row?.validation_errors,
      metadata: row?.metadata,
      ableImport: row?.able_payroll_imports,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async createIrcSwtSession(session) {
    const { data, error } = await supabase?.from('irc_swt_form_sessions')?.insert({
        session_name: session?.sessionName,
        pay_period_start: session?.payPeriodStart,
        pay_period_end: session?.payPeriodEnd,
        form_status: session?.formStatus || 'draft',
        able_import_id: session?.ableImportId,
        payroll_run_id: session?.payrollRunId,
        generated_by: session?.generatedBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      sessionName: data?.session_name,
      formStatus: data?.form_status
    };
  },

  async updateIrcSwtSessionStatus(id, status, updates = {}) {
    const updateData = {
      form_status: status
    };

    if (status === 'generated') {
      updateData.generated_at = new Date()?.toISOString();
    } else if (status === 'validated') {
      updateData.validated_at = new Date()?.toISOString();
    } else if (status === 'submitted') {
      updateData.submitted_at = new Date()?.toISOString();
    }

    if (updates?.totalEmployees) updateData.total_employees = updates?.totalEmployees;
    if (updates?.totalTaxableIncome) updateData.total_taxable_income = updates?.totalTaxableIncome;
    if (updates?.totalSwtWithheld) updateData.total_swt_withheld = updates?.totalSwtWithheld;
    if (updates?.formUrl) updateData.form_url = updates?.formUrl;
    if (updates?.ircReferenceNumber) updateData.irc_reference_number = updates?.ircReferenceNumber;
    if (updates?.validationErrors) updateData.validation_errors = updates?.validationErrors;

    const { data, error } = await supabase?.from('irc_swt_form_sessions')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      formStatus: data?.form_status
    };
  },

  // ============================================================================
  // VIOLATION TRENDS
  // ============================================================================

  async getViolationTrends(periodStart, periodEnd) {
    const period = `${periodStart} to ${periodEnd}`;
    const { data, error} = await supabase?.from('violation_trend_analysis')?.select('*')?.eq('analysis_period', period)?.order('total_cost_kina', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      analysisPeriod: row?.analysis_period,
      violationType: row?.violation_type,
      department: row?.department,
      occurrenceCount: row?.occurrence_count,
      affectedEmployees: row?.affected_employees,
      totalCostKina: row?.total_cost_kina,
      trendDirection: row?.trend_direction,
      percentageChange: row?.percentage_change,
      severityDistribution: row?.severity_distribution,
      createdAt: row?.created_at
    })) || [];
  },

  async generateViolationTrends(periodStart, periodEnd) {
    const { data, error } = await supabase?.rpc('generate_violation_trends', {
      p_period_start: periodStart,
      p_period_end: periodEnd
    });
    
    if (error) throw error;
    
    return data;
  },

  // ============================================================================
  // DEPARTMENT PERFORMANCE METRICS
  // ============================================================================

  async getDepartmentMetrics(reportId) {
    const { data, error } = await supabase?.from('department_performance_metrics')?.select('*')?.eq('report_id', reportId)?.order('compliance_score', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      department: row?.department,
      periodStart: row?.period_start,
      periodEnd: row?.period_end,
      complianceScore: row?.compliance_score,
      payrollAccuracyRate: row?.payroll_accuracy_rate,
      leaveComplianceRate: row?.leave_compliance_rate,
      contractComplianceRate: row?.contract_compliance_rate,
      totalViolations: row?.total_violations,
      criticalViolations: row?.critical_violations,
      resolvedViolations: row?.resolved_violations,
      averageResolutionDays: row?.average_resolution_days,
      totalCostImpactKina: row?.total_cost_impact_kina,
      employeeCount: row?.employee_count,
      trendVsPreviousPeriod: row?.trend_vs_previous_period,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // COST IMPACT ANALYSIS
  // ============================================================================

  async getCostImpactAnalysis(reportId) {
    const { data, error } = await supabase?.from('cost_impact_analysis')?.select('*')?.eq('report_id', reportId)?.order('total_cost_kina', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      analysisPeriod: row?.analysis_period,
      costCategory: row?.cost_category,
      department: row?.department,
      totalCostKina: row?.total_cost_kina,
      backpayCostKina: row?.backpay_cost_kina,
      penaltyCostKina: row?.penalty_cost_kina,
      administrativeCostKina: row?.administrative_cost_kina,
      affectedEmployees: row?.affected_employees,
      costPerEmployeeKina: row?.cost_per_employee_kina,
      costBreakdown: row?.cost_breakdown,
      mitigationRecommendations: row?.mitigation_recommendations || [],
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // DLIR SUBMISSION READINESS
  // ============================================================================

  async getDlirReadiness(reportId) {
    const { data, error } = await supabase?.from('dlir_submission_readiness')?.select('*')?.eq('report_id', reportId)?.order('item_category', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      checklistItem: row?.checklist_item,
      itemCategory: row?.item_category,
      isComplete: row?.is_complete,
      completionPercentage: row?.completion_percentage,
      requiredDocuments: row?.required_documents || [],
      missingDocuments: row?.missing_documents || [],
      validationStatus: row?.validation_status,
      notes: row?.notes,
      completedAt: row?.completed_at,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async calculateDlirReadiness(reportId) {
    const { data, error } = await supabase?.rpc('calculate_dlir_readiness', {
      p_report_id: reportId
    });
    
    if (error) throw error;
    
    return data;
  }
};

export default complianceReportsCustomizationService;